import type { InternalAxiosRequestConfig } from 'axios';

export const BRAND_IDENTIFIER_HEADER = 'X-Brand-Identifier';
export const APPLICATION_IDENTIFIER_HEADER = 'X-Application-Identifier';

export type ActiveScope = {
  brandIdentifier: string;
  applicationIdentifier: string;
};

/**
 * The brand and application the user is currently acting in.
 *
 * Axios interceptors cannot read React context, so the app mirrors the active scope into this module
 * variable (see `ActiveScopeSync` in chat-components). Module state is per browser tab/window, so two
 * tabs can act in different scopes without interfering. The server only treats the resulting headers
 * as a scope selector and verifies access itself.
 */
let activeScope: ActiveScope | null = null;

export function setActiveScope(scope: ActiveScope | null): void {
  activeScope = scope;
}

export function getActiveScope(): ActiveScope | null {
  return activeScope;
}

/**
 * Axios request interceptor that sends the active scope as `X-Brand-Identifier` and
 * `X-Application-Identifier`. Both headers are sent together or not at all, and a header the caller
 * already set on the request is left untouched.
 */
export function activeScopeRequestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const scope = activeScope;
  if (!scope || !scope.brandIdentifier || !scope.applicationIdentifier) return config;

  if (!config.headers.has(BRAND_IDENTIFIER_HEADER) && !config.headers.has(APPLICATION_IDENTIFIER_HEADER)) {
    config.headers.set(BRAND_IDENTIFIER_HEADER, scope.brandIdentifier);
    config.headers.set(APPLICATION_IDENTIFIER_HEADER, scope.applicationIdentifier);
  }
  return config;
}
